import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import ExamSectionBreakdownChart from "@/components/exam/ExamSectionBreakdownChart";
import ExamWeakAreasCallouts from "@/components/exam/ExamWeakAreasCallouts";
import type {
  ExamQuestionForClient,
  SubmitExamResponse,
} from "@/lib/exam-api";

/**
 * apps/web :: components/exam :: ExamResultsCard
 *
 * Post-submit view. Renders:
 *
 *   - A top banner with pass/fail. Color-blind-safe: the banner uses an
 *     ICON (check / cross) AND text — never colour alone (Hard Rule #7).
 *   - The score (correct / total, plus percentage).
 *   - A per-question review: every question is listed with the user's
 *     chosen choice + the correct choice + the explanation + (optional)
 *     handbook citation. When the user was wrong we mark the chosen row
 *     `data-status="wrong"` and the correct row `data-status="correct"`
 *     so tests can assert the semantic regardless of Tailwind classes.
 *   - A CTA row: "Try again" if the user passed (or if cooldown has
 *     already elapsed); otherwise a countdown to retryAvailableAtMs.
 *     "Back to dashboard" secondary link is always visible.
 *
 * Accessibility: the pass/fail badge sits inside an `aria-live="polite"`
 * region so screen readers announce the outcome on mount. Focus is not
 * moved — the user just submitted, they're already the focus target.
 */
export interface ExamResultsCardProps {
  stateCode: string;
  result: SubmitExamResponse;
  questions: ExamQuestionForClient[];
  /** Called when the user clicks "Try again" (passed OR cooldown expired). */
  onRetry: () => void;
}

function formatMMSS(remainingMs: number): string {
  const seconds = Math.max(0, Math.ceil(remainingMs / 1_000));
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function ExamResultsCard({
  stateCode,
  result,
  questions,
  onRetry,
}: ExamResultsCardProps) {
  const { t } = useTranslation();

  const badgeIcon = result.passed ? "✓" : "✕";
  const badgeLabel = result.passed
    ? t("exam.results.passBadge")
    : t("exam.results.failBadge");
  const badgeClass = result.passed
    ? "bg-success/10 text-success border-success/40"
    : "bg-danger/10 text-danger border-danger/40";

  const percent = Math.round(
    (result.correctCount / Math.max(1, result.questionCount)) * 100,
  );

  return (
    <section
      data-testid="exam-results"
      aria-labelledby="exam-results-heading"
      className="mt-lg flex flex-col gap-md"
    >
      <div
        data-testid="exam-results-live"
        aria-live="polite"
        role="status"
        className="sr-only"
      >
        {badgeLabel}
      </div>

      <div
        data-testid="exam-results-banner"
        className={`flex items-center justify-between gap-sm rounded-lg border p-md shadow-sm sm:p-lg ${badgeClass}`}
      >
        <div className="flex items-center gap-sm">
          <span
            data-testid="exam-results-icon"
            aria-hidden="true"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-lg font-bold"
          >
            {badgeIcon}
          </span>
          <div>
            <h2
              id="exam-results-heading"
              className="font-display text-xl font-bold uppercase tracking-wide"
            >
              {badgeLabel}
            </h2>
            <p
              data-testid="exam-results-score"
              className="mt-xs text-sm"
            >
              {t("exam.results.score", {
                correct: result.correctCount,
                total: result.questionCount,
                percent,
                passPercent: result.passPercent,
              })}
            </p>
          </div>
        </div>
      </div>

      {/* CTA row. */}
      <div className="flex flex-wrap items-center gap-sm">
        {result.passed || result.retryAvailableAtMs == null ? (
          <button
            type="button"
            data-testid="exam-results-retry"
            onClick={onRetry}
            className="inline-flex items-center rounded-md border border-brand-primary bg-brand-primary px-md py-sm text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
          >
            {t("exam.results.tryAgain")}
          </button>
        ) : (
          <CooldownCta
            retryAvailableAtMs={result.retryAvailableAtMs}
            onExpired={onRetry}
          />
        )}
        {result.passed && (
          // ROADS-430: prominent "What's next" CTA when the user
          // passed. Routes to the post-pass guidance hub
          // (/whats-next is wired in ROADS-431; until then we render
          // the link and the existing fallback handles missing routes).
          <Link
            to="/whats-next"
            data-testid="exam-results-whats-next"
            className="inline-flex items-center rounded-md border border-brand-accent bg-brand-accent px-md py-sm text-sm font-semibold text-white no-underline transition-colors hover:bg-brand-accent/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
          >
            {t("exam.results.whatsNextCta")} →
          </Link>
        )}
        <Link
          to="/dashboard"
          data-testid="exam-results-dashboard-link"
          className="inline-flex items-center rounded-md border border-brand-primary bg-white px-md py-sm text-sm font-semibold text-brand-primary no-underline transition-colors hover:bg-brand-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {t("exam.results.backToDashboard")}
        </Link>
        {/*
          Wave 15b — tertiary text link to the dedicated /exam/history
          page. Sits in the same CTA row so it's discoverable right
          after a submission without competing with the primary "Try
          again" button.
        */}
        <Link
          to="/exam/history"
          data-testid="exam-results-history-link"
          className="text-sm font-semibold text-brand-primary underline underline-offset-2 hover:no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        >
          {t("exam.results.historyLink")}
        </Link>
      </div>

      {/* ROADS-430 — per-section breakdown bar chart + weak-areas
          callouts. Rendered above the per-question review so the user
          sees the high-level diagnostic before walking individual
          questions. Both gracefully degrade when the backend doesn't
          emit them (older deploy or attempt without category metadata). */}
      {result.perSectionBreakdown.length > 0 && (
        <section
          data-testid="exam-results-breakdown"
          aria-labelledby="exam-breakdown-heading"
          className="rounded-lg border border-brand-primary/10 bg-white p-md shadow-sm sm:p-lg"
        >
          <h3
            id="exam-breakdown-heading"
            className="font-display text-lg font-bold text-brand-primary"
          >
            {t("exam.results.breakdownHeading")}
          </h3>
          <div className="mt-md">
            <ExamSectionBreakdownChart
              breakdown={result.perSectionBreakdown}
            />
          </div>
        </section>
      )}

      {result.weakAreas.length > 0 && (
        <ExamWeakAreasCallouts weakAreas={result.weakAreas} />
      )}

      {/* Per-question review. */}
      <div
        data-testid="exam-results-review"
        aria-labelledby="exam-results-review-heading"
        className="rounded-lg border border-brand-primary/10 bg-white p-md shadow-sm sm:p-lg"
      >
        <h3
          id="exam-results-review-heading"
          className="font-display text-lg font-bold text-brand-primary"
        >
          {t("exam.results.reviewHeading")}
        </h3>
        <ol className="mt-md flex flex-col gap-md">
          {result.review.map((r, index) => {
            const q = questions.find((x) => x.id === r.questionId);
            const chosenChoice = q?.choices.find(
              (c) => c.id === r.chosenChoiceId,
            );
            const correctChoice = q?.choices.find(
              (c) => c.id === r.correctChoiceId,
            );
            return (
              <li
                key={r.questionId}
                data-testid={`exam-review-${r.questionId}`}
                className="rounded-md border border-brand-primary/10 bg-brand-surface/30 p-md"
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-neutral-600">
                  {t("exam.results.reviewQuestionNumber", { n: index + 1 })}
                </p>
                <p className="mt-xs font-semibold text-brand-primary">
                  {q?.question_text ?? r.questionId}
                </p>

                {chosenChoice && (
                  <p
                    data-testid={`exam-review-${r.questionId}-chosen`}
                    data-status={r.isCorrect ? "correct" : "wrong"}
                    className={`mt-md text-sm ${
                      r.isCorrect
                        ? "text-success"
                        : "text-danger"
                    }`}
                  >
                    <span className="font-semibold">
                      {t("exam.results.chosenPrefix")}:
                    </span>{" "}
                    <span aria-hidden="true">
                      {r.isCorrect ? "✓" : "✕"}
                    </span>{" "}
                    {chosenChoice.text}
                  </p>
                )}
                {!chosenChoice && r.chosenChoiceId === null && (
                  <p
                    data-testid={`exam-review-${r.questionId}-unanswered`}
                    className="mt-md text-sm text-danger"
                  >
                    <span aria-hidden="true">✕</span>{" "}
                    {t("exam.results.unanswered")}
                  </p>
                )}
                {!r.isCorrect && correctChoice && (
                  <p
                    data-testid={`exam-review-${r.questionId}-correct`}
                    data-status="correct"
                    className="mt-xs text-sm text-success"
                  >
                    <span className="font-semibold">
                      {t("exam.results.correctPrefix")}:
                    </span>{" "}
                    <span aria-hidden="true">✓</span> {correctChoice.text}
                  </p>
                )}
                <p className="mt-md text-sm text-neutral-800">
                  {r.explanation}
                </p>
                {r.handbookSection && (
                  <p className="mt-xs text-xs text-neutral-600">
                    {t("exam.results.handbookPrefix")}: {r.handbookSection}
                  </p>
                )}
              </li>
            );
          })}
        </ol>
      </div>

      <p className="text-xs text-neutral-600">
        {t("exam.results.stateFooter", { state: stateCode })}
      </p>
    </section>
  );
}

function CooldownCta({
  retryAvailableAtMs,
  onExpired,
}: {
  retryAvailableAtMs: number;
  onExpired: () => void;
}) {
  const { t } = useTranslation();
  const [remainingMs, setRemainingMs] = useState(() =>
    Math.max(0, retryAvailableAtMs - Date.now()),
  );
  const [expired, setExpired] = useState(remainingMs <= 0);

  useEffect(() => {
    const tick = () => {
      const next = Math.max(0, retryAvailableAtMs - Date.now());
      setRemainingMs(next);
      if (next <= 0) setExpired(true);
    };
    tick();
    const id = setInterval(tick, 1_000);
    return () => clearInterval(id);
  }, [retryAvailableAtMs]);

  if (expired) {
    return (
      <button
        type="button"
        data-testid="exam-results-retry"
        onClick={onExpired}
        className="inline-flex items-center rounded-md border border-brand-primary bg-brand-primary px-md py-sm text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
      >
        {t("exam.results.tryAgain")}
      </button>
    );
  }
  return (
    <span
      data-testid="exam-results-cooldown"
      className="inline-flex items-center rounded-md border border-brand-accent/40 bg-brand-accent/5 px-md py-sm text-sm font-semibold text-brand-primary"
    >
      {t("exam.results.cooldownCta", { time: formatMMSS(remainingMs) })}
    </span>
  );
}
